
// Generate and handle OTP codes

/**
 * Generates a random N-digit OTP code
 * @param length Number of digits (default: 6)
 * @returns A string containing the OTP
 */
export const generateOtp = (length: number = 6): string => {
  const multiplier = Math.pow(10, length - 1);
  const otp = Math.floor(multiplier + Math.random() * 9 * multiplier).toString();
  console.log("=== OTP GENERATED ===");
  console.log("Generated OTP:", otp);
  console.log("OTP type:", typeof otp);
  console.log("OTP length:", otp.length);
  return otp;
};

/**
 * Stores an OTP code in localStorage with expiration
 * @param otp The OTP code to store
 * @param expirationMinutes Minutes until expiration (default: 10)
 */
export const storeOtp = (otp: string, expirationMinutes: number = 10): void => {
  try {
    console.log("=== STORING OTP ===");
    console.log("OTP to store:", otp);
    console.log("OTP type:", typeof otp);
    console.log("Expiration minutes:", expirationMinutes);
    
    const expirationTime = new Date();
    expirationTime.setMinutes(expirationTime.getMinutes() + expirationMinutes);
    const expirationISO = expirationTime.toISOString();
    
    console.log("Calculated expiration time:", expirationISO);
    
    // Clear any existing OTP data first
    localStorage.removeItem('currentOtp');
    localStorage.removeItem('otpExpiry');
    localStorage.removeItem('userOtpCode');
    localStorage.removeItem('userOtpExpiration');
    
    // Store the new OTP
    localStorage.setItem('currentOtp', otp);
    localStorage.setItem('otpExpiry', expirationISO);
    
    // Verify storage immediately
    const storedOtp = localStorage.getItem('currentOtp');
    const storedExpiry = localStorage.getItem('otpExpiry');
    console.log("Verification after storage:");
    console.log("- Stored OTP:", storedOtp);
    console.log("- Stored OTP type:", typeof storedOtp);
    console.log("- Stored expiry:", storedExpiry);
    console.log("- Storage successful:", storedOtp === otp);
    
    // Also log all localStorage keys for debugging
    console.log("All localStorage keys:", Object.keys(localStorage));
  } catch (error) {
    console.error("Error storing OTP in localStorage:", error);
  }
};

/**
 * Retrieves the stored OTP and checks if it's still valid
 * @returns An object containing the OTP and whether it's expired
 */
export const getStoredOtp = (): { otp: string | null; isExpired: boolean } => {
  try {
    console.log("=== RETRIEVING OTP ===");
    
    // Log all localStorage content for debugging
    console.log("All localStorage keys:", Object.keys(localStorage));
    console.log("localStorage content:");
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        console.log(`- ${key}: ${localStorage.getItem(key)}`);
      }
    }
    
    const storedOtp = localStorage.getItem('currentOtp');
    const storedExpiry = localStorage.getItem('otpExpiry');
    
    console.log("Retrieved values:");
    console.log("- OTP:", storedOtp);
    console.log("- OTP type:", typeof storedOtp);
    console.log("- Expiry:", storedExpiry);
    console.log("- Expiry type:", typeof storedExpiry);
    
    if (!storedOtp || !storedExpiry) {
      console.log("Missing OTP or expiry in storage");
      return { otp: null, isExpired: true };
    }
    
    const expiryTime = new Date(storedExpiry).getTime();
    const currentTime = Date.now();
    const isExpired = currentTime > expiryTime;
    
    console.log("Time comparison:");
    console.log("- Current time:", new Date(currentTime).toLocaleTimeString());
    console.log("- Expiry time:", new Date(expiryTime).toLocaleTimeString());
    console.log("- Is expired:", isExpired);
    console.log("- Time difference (ms):", expiryTime - currentTime);
    
    return { otp: storedOtp, isExpired };
  } catch (error) {
    console.error("Error retrieving OTP from localStorage:", error);
    return { otp: null, isExpired: true };
  }
};

/**
 * Clears the stored OTP from localStorage
 */
export const clearStoredOtp = (): void => {
  try {
    console.log("=== CLEARING OTP ===");
    localStorage.removeItem('currentOtp');
    localStorage.removeItem('otpExpiry');
    // Also clear any old keys that might exist
    localStorage.removeItem('userOtpCode');
    localStorage.removeItem('userOtpExpiration');
    console.log("OTP cleared from storage");
  } catch (error) {
    console.error("Error clearing OTP from localStorage:", error);
  }
};

/**
 * Clears all admin authentication data from localStorage
 */
export const clearAdminAuthData = (): void => {
  try {
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminAuthTime');
    localStorage.removeItem('adminTwoFactorVerified');
    localStorage.removeItem('adminOtpCode');
    localStorage.removeItem('adminOtpExpiration');
    localStorage.removeItem('adminRequestedProfileTab');
    localStorage.removeItem('adminEmail');
    console.log("Admin authentication data cleared from storage");
  } catch (error) {
    console.error("Error clearing admin auth data from localStorage:", error);
  }
};
